import { supabase } from './supabase';

const SESSION_KEY = 'ssai_lead_session_key';
const LEAD_ID_KEY = 'ssai_onboarding_lead_id';

export interface LeadRestoreResult {
  ok: boolean;
  session_key?: string;
  lead?: {
    id: string;
    business_name?: string | null;
    contact_name?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    industry?: string | null;
    current_step?: number;
    progress_percent?: number;
    draft_payload?: Record<string, unknown>;
    selected_services?: unknown[];
    selected_platforms?: unknown[];
  } | null;
}

export interface FinalizeOnboardingResult {
  ok: boolean;
  user_id?: string | null;
  n8n_client_id?: string | null;
  client_profile_provisioned?: boolean;
  wf11_status?: 'sent' | 'skipped' | 'failed' | 'unknown' | string;
  wf11_detail?: 'canonical_provisioning' | 'suppressed' | string;
  wf11_client_id?: string | null;
  admin_review_notification_status?: 'sent' | 'retry' | 'failed' | 'unknown' | 'suppressed' | 'in_progress' | 'skipped' | string;
  credential_email_status?: 'sent' | 'retry' | 'failed' | 'unknown' | 'suppressed' | 'in_progress' | string;
  credential_delivery?: {
    email_status?: 'sent' | 'retry' | 'failed' | 'unknown' | 'suppressed' | 'in_progress' | string;
    email_provider?: string;
    email?: string;
    delivery_method?: 'secure_magic_link' | string;
    connect_url?: string;
    dashboard_url?: string;
    delivery_note?: string;
  };
}

function readSearchParam(name: string) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch {
    return null;
  }
}

export function getLeadSessionKey() {
  const handoff = readSearchParam('ssid') || readSearchParam('ssai_sid') || readSearchParam('lead_session_id');
  if (handoff) {
    localStorage.setItem(SESSION_KEY, handoff);
    return handoff;
  }

  const existing = localStorage.getItem(SESSION_KEY);
  if (existing) return existing;

  const created = crypto.randomUUID();
  localStorage.setItem(SESSION_KEY, created);
  return created;
}

export function getLeadId() {
  const handoffLead = readSearchParam('lead');
  if (handoffLead) {
    localStorage.setItem(LEAD_ID_KEY, handoffLead);
    return handoffLead;
  }
  return localStorage.getItem(LEAD_ID_KEY);
}

function rememberLeadId(leadId?: string | null) {
  if (leadId) localStorage.setItem(LEAD_ID_KEY, leadId);
}

function attribution() {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = value;
  });

  return {
    url: window.location.href,
    path: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || null,
    utm,
  };
}

export async function autosaveOnboardingLead(input: {
  draft: Record<string, unknown>;
  currentStep: number;
  progressPercent: number;
  selectedServices?: unknown[];
  selectedPlatforms?: string[];
  userId?: string | null;
  changedFields?: string[];
}) {
  const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
    body: {
      action: 'autosave',
      session_key: getLeadSessionKey(),
      lead_id: getLeadId(),
      source: 'onboarding',
      draft: input.draft,
      current_step: input.currentStep,
      progress_percent: input.progressPercent,
      selected_services: input.selectedServices || [],
      selected_platforms: input.selectedPlatforms || [],
      user_id: input.userId || undefined,
      changed_fields: input.changedFields || [],
      ...attribution(),
    },
  });

  if (error) throw error;
  rememberLeadId(data?.lead_id);
  return data;
}

export async function restoreOnboardingLead(): Promise<LeadRestoreResult> {
  const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
    body: {
      action: 'restore',
      session_key: getLeadSessionKey(),
      lead_id: getLeadId(),
      source: 'onboarding',
      ...attribution(),
    },
  });

  if (error) throw error;
  rememberLeadId(data?.lead?.id || data?.lead_id);
  return data as LeadRestoreResult;
}

export async function finalizeOnboardingLead(input: {
  email?: string | null;
  businessName?: string | null;
  contactName?: string | null;
  phone?: string | null;
  address?: string | null;
  selectedServiceTypes?: unknown[];
  selectedTier?: unknown;
  selectedServices?: unknown[];
  n8nClientId?: string | null;
  textNotificationsAllowed?: boolean;
}): Promise<FinalizeOnboardingResult> {
  const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
    body: {
      action: 'finalize',
      session_key: getLeadSessionKey(),
      lead_id: getLeadId(),
      email: input.email,
      business_name: input.businessName,
      contact_name: input.contactName,
      phone: input.phone,
      address: input.address,
      selected_service_types: input.selectedServiceTypes || [],
      selected_tier: input.selectedTier || null,
      selected_services: input.selectedServices || [],
      n8n_client_id: input.n8nClientId,
      text_notifications_allowed: input.textNotificationsAllowed === true,
      ...attribution(),
    },
  });

  if (error) throw error;
  rememberLeadId(data?.lead_id);
  return data as FinalizeOnboardingResult;
}

export async function unsubscribeOnboardingReminders(token: string) {
  const { data, error } = await supabase.functions.invoke('onboarding-lead-capture', {
    body: {
      action: 'unsubscribe',
      token,
      session_key: getLeadSessionKey(),
    },
  });
  if (error) throw error;
  return data;
}
